import {
  EMPLOYEE_FILTER_ACCESS_OPTIONS,
  EMPLOYEE_FILTER_ROLE_OPTIONS,
  EMPLOYEE_FILTER_STATUS_OPTIONS,
} from '@/constants';
import { KLAYO_COLORS } from '@/themes';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Checkbox,
  Drawer,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { Switch } from 'klayowebshared';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { Button } from './Button';

export interface EmployeeFilter {
  status: number | null;
  includeInactive: boolean;
  accessLevels: number[];
  roles: number[];
}

export const EMPTY_EMPLOYEE_FILTER: EmployeeFilter = {
  status: null,
  includeInactive: false,
  accessLevels: [],
  roles: [],
};

interface FilterFormValues {
  status: string; // 'null' | '0' | '1' | '2' | '3'
  includeInactive: boolean;
  accessLevels: number[];
  roles: number[];
}

const toFormValues = (f: EmployeeFilter): FilterFormValues => ({
  status: f.status === null ? 'null' : String(f.status),
  includeInactive: f.includeInactive,
  accessLevels: f.accessLevels,
  roles: f.roles,
});

const fromFormValues = (v: FilterFormValues): EmployeeFilter => ({
  status: v.status === 'null' ? null : Number(v.status),
  includeInactive: v.includeInactive,
  accessLevels: v.accessLevels,
  roles: v.roles,
});


interface EmployeeFilterDrawerProps {
  open: boolean;
  filters: EmployeeFilter;
  translate: (key: string, params?: Record<string, unknown>) => string;
  onClose: () => void;
  onApply: (filters: EmployeeFilter) => void;
}

export function EmployeeFilterDrawer({
  open,
  filters,
  translate,
  onClose,
  onApply,
}: EmployeeFilterDrawerProps) {
  const { control, handleSubmit, reset, setValue } = useForm<FilterFormValues>({
    defaultValues: toFormValues(filters),
  });

  const statusValue = useWatch({ control, name: 'status' });

  const handleOpen = () => reset(toFormValues(filters));

  const handleApply = handleSubmit((values) => {
    onApply(fromFormValues(values));
    onClose();
  });

  const handleCancel = () => {
    reset(toFormValues(filters));
    onClose();
  };

  const toggleArrayValue = (arr: number[], value: number): number[] =>
    arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];

  return (
    <Drawer
      open={open}
      anchor='right'
      onClose={handleCancel}
      SlideProps={{ onEnter: handleOpen }}
      PaperProps={{ sx: { width: { xs: '100vw', sm: 360 }, maxWidth: '100vw' } }}
    >
      <Stack direction='column' sx={{ height: '100%' }}>
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${KLAYO_COLORS.GreyGR200}`,
          }}
        >
          <Typography variant='h6' fontWeight={600}>
            {translate('common.filters')}
          </Typography>
          <IconButton size='small' onClick={handleCancel}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Stack spacing={3} sx={{ px: 3, py: 3, flexGrow: 1, overflowY: 'auto' }}>
          {/* Status */}
          <Stack spacing={1}>
            <Typography variant='body2' fontWeight={600}>
              {translate('common.status')}
            </Typography>
            <Controller
              name='status'
              control={control}
              render={({ field }) => (
                <RadioGroup
                  {...field}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    if (e.target.value !== 'null') setValue('includeInactive', false);
                  }}
                >
                  {EMPLOYEE_FILTER_STATUS_OPTIONS.map((opt) => (
                    <FormControlLabel
                      key={opt.value}
                      value={opt.value}
                      control={<Radio size='small' />}
                      label={<Typography variant='body2'>{translate(opt.labelKey)}</Typography>}
                    />
                  ))}
                </RadioGroup>
              )}
            />
            <Controller
              name='includeInactive'
              control={control}
              render={({ field }) => (
                <Switch
                  label={translate('settingsViews.employees.includeInactive')}
                  checked={field.value}
                  disabled={statusValue !== 'null'}
                  onChange={(e: any) => field.onChange(e.target.checked)}
                />
              )}
            />
          </Stack>

          {/* Access */}
          <Stack spacing={1}>
            <Typography variant='body2' fontWeight={600}>
              {translate('common.access')}
            </Typography>
            <Controller
              name='accessLevels'
              control={control}
              render={({ field }) => (
                <>
                  {EMPLOYEE_FILTER_ACCESS_OPTIONS.map((opt) => (
                    <FormControlLabel
                      key={opt.value}
                      control={
                        <Checkbox
                          size='small'
                          checked={field.value.includes(opt.value)}
                          onChange={() =>
                            field.onChange(toggleArrayValue(field.value, opt.value))
                          }
                        />
                      }
                      label={<Typography variant='body2'>{translate(opt.labelKey)}</Typography>}
                    />
                  ))}
                </>
              )}
            />
          </Stack>

          {/* Role */}
          <Stack spacing={1}>
            <Typography variant='body2' fontWeight={600}>
              {translate('common.role')}
            </Typography>
            <Controller
              name='roles'
              control={control}
              render={({ field }) => (
                <>
                  {EMPLOYEE_FILTER_ROLE_OPTIONS.map((opt) => (
                    <FormControlLabel
                      key={opt.value}
                      control={
                        <Checkbox
                          size='small'
                          checked={field.value.includes(opt.value)}
                          onChange={() =>
                            field.onChange(toggleArrayValue(field.value, opt.value))
                          }
                        />
                      }
                      label={<Typography variant='body2'>{translate(opt.labelKey)}</Typography>}
                    />
                  ))}
                </>
              )}
            />
          </Stack>
        </Stack>

        {/* Footer */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: `1px solid ${KLAYO_COLORS.GreyGR200}`,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
          }}
        >
          <Button variant='outlined' label={translate('common.cancel')} size='md' onClick={handleCancel} />
          <Button variant='filled' label={translate('common.apply')} size='md' onClick={handleApply} />
        </Box>
      </Stack>
    </Drawer>
  );
}
